import { Checkbox as AntDCheckbox, Form } from 'antd'

import { Formik, useFormikContext } from 'formik'
import mapValues from 'lodash/mapValues'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'

import api from '../../api'
import { formElementPropsGenerator } from '../../lib/forms'
import { Yup } from '../../lib/validations'
import { Input, SubmitButton } from '../Forms'
import DeviceTypeSelect from '../Forms/DeviceTypeSelect'
import ManufacturerSelect from '../Forms/ManufacturerSelect'

const ICM_ELIGIBLE_CATEGORIES = ['ICD', 'PM']

const DEVICE_MODEL_LABELS = {
  manufacturer: 'Manufacturer',
  name: 'Name',
  deviceTypeId: 'Device Type',
  icmCapable: 'ICM Capable',
}

const INITIAL_VALUES = {
  ...mapValues(DEVICE_MODEL_LABELS, () => undefined),
  icmCapable: false,
}

const propsFor = formElementPropsGenerator({
  formName: 'device-models-form',
  labels: DEVICE_MODEL_LABELS,
})

const labelCol = {
  sm: { span: 4, offset: 2 },
}

const wrapperCol = {
  sm: { span: 16 },
}

const actionsWrapperCol = {
  sm: { span: 4, offset: labelCol.sm.span + labelCol.sm.offset },
}

const validationSchema = Yup.object().shape({
  manufacturer: Yup.string().required(),
  deviceTypeId: Yup.string().required(),
  name: Yup.string().required(),
  icmCapable: Yup.boolean(),
})

function IcmCapableCheckbox({ deviceTypes }) {
  const { values, setFieldValue } = useFormikContext()

  const selectedDeviceType = useMemo(
    () => deviceTypes.find(dt => dt.id === values.deviceTypeId),
    [deviceTypes, values.deviceTypeId],
  )

  const isIcmEligible = useMemo(
    () => ICM_ELIGIBLE_CATEGORIES.includes(selectedDeviceType?.category),
    [selectedDeviceType],
  )

  useEffect(() => {
    if (deviceTypes.length > 0 && !isIcmEligible && values.icmCapable) {
      setFieldValue('icmCapable', false)
    }
  }, [deviceTypes, isIcmEligible, values.icmCapable, setFieldValue])

  if (!isIcmEligible) return null

  return (
    <Form.Item
      wrapperCol={{
        sm: { offset: labelCol.sm.span + labelCol.sm.offset },
      }}
    >
      <AntDCheckbox
        checked={values.icmCapable}
        onChange={e => setFieldValue('icmCapable', e.target.checked)}
      >
        {DEVICE_MODEL_LABELS.icmCapable}
      </AntDCheckbox>
    </Form.Item>
  )
}

IcmCapableCheckbox.propTypes = {
  deviceTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      category: PropTypes.string,
    }),
  ).isRequired,
}

export default function DeviceModelsForm({
  deviceModel = undefined,
  onSubmit,
}) {
  const isNew = !deviceModel
  const [deviceTypes, setDeviceTypes] = useState([])

  const fetchDeviceTypes = useCallback(async () => {
    const data = await api.deviceTypes.list()
    setDeviceTypes(data)
  }, [])

  useEffect(() => {
    fetchDeviceTypes()
  }, [fetchDeviceTypes])

  return (
    <div>
      <Formik
        initialValues={deviceModel || INITIAL_VALUES}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ handleSubmit, isSubmitting, isValid, submitCount }) => (
          <Form
            onFinish={handleSubmit}
            labelCol={labelCol}
            wrapperCol={wrapperCol}
          >
            <Input {...propsFor('name')} autoFocus={isNew} />
            <ManufacturerSelect
              {...propsFor('manufacturer')}
              size="medium"
              style={{ minWidth: 200 }}
              disableAll
            />
            <DeviceTypeSelect
              {...propsFor('deviceTypeId')}
              clearWhenUnmount
              size="medium"
              style={{ minWidth: 200 }}
            />
            <IcmCapableCheckbox deviceTypes={deviceTypes} />
            <SubmitButton
              isSubmitting={isSubmitting}
              isValid={isValid}
              title={isNew ? 'Create Device Model' : 'Save Device Model'}
              submitCount={submitCount}
              wrapperCol={actionsWrapperCol}
            />
          </Form>
        )}
      </Formik>
    </div>
  )
}

DeviceModelsForm.propTypes = {
  deviceModel: PropTypes.shape({
    manufacturer: PropTypes.string,
    id: PropTypes.string,
    icmCapable: PropTypes.bool,
  }),
  onSubmit: PropTypes.func.isRequired,
}
